import { CreativeWorkspaceHeader } from "./CreativeWorkspaceHeader";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "@digital-twins/design-system/icons";
import type {
  CreativeItem,
  CreativeSubject,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { Skeleton } from "@/components/ui/skeleton";
import { useCreativeLibrary } from "@/hooks/useCreativeLibrary";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import { cn } from "@/lib/utils";
import type {
  CreativeWorkspaceProps,
  CreativeBoardEditorState,
} from "@/types/creative-library";
import { CreativeBoardNavigation } from "./CreativeBoardNavigation";
import { CreativeWorkspaceFilters } from "./CreativeWorkspaceFilters";
import { CreativeItemCards } from "./CreativeItemCards";
import { CreativeLibraryEmptyState } from "./CreativeLibraryEmptyState";
import { CreativeUpload } from "./CreativeUpload";
import { CreativeDetail } from "./CreativeDetail";
import { CreativeLibrarySubjects } from "./CreativeLibrarySubjects";
import { BrandEditor } from "./BrandEditor";
import { BoardEditor } from "./BoardEditor";
import { useBoardContents } from "./useBoardContents";
import { useStartCreativeThread } from "./useStartCreativeThread";
import {
  CREATIVE_PAGE_SIZE,
  EMPTY_CREATIVE_FILTERS,
  filterCreativeItems,
  selectCreativeBoard,
} from "./creativeBoardWorkspace";

export function CreativeWorkspace({ className }: CreativeWorkspaceProps) {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const boardToken = params.get("board");
  const focused = params.get("creative");
  const requestedBrand = params.get("brand");
  const library = useCreativeLibrary();
  const [refresh, setRefresh] = useState(0);
  const contents = useBoardContents(boardToken, refresh);
  const [filters, setFilters] = useState({
    ...EMPTY_CREATIVE_FILTERS,
    brand: requestedBrand ?? "all",
  });
  const [limit, setLimit] = useState(CREATIVE_PAGE_SIZE);
  const [upload, setUpload] = useState(false);
  const [newBrand, setNewBrand] = useState(false);
  const [boardEditor, setBoardEditor] = useState<CreativeBoardEditorState>({
    open: false,
    value: null,
  });
  const [detail, setDetail] = useState<CreativeItem | null>(null);
  const [detailError, setDetailError] = useState(false);
  const { start, starting, actionError, clearActionError } =
    useStartCreativeThread();
  const board = contents.board;
  const blocked = Boolean(
    boardToken &&
    (contents.status === "error" || contents.status === "unavailable"),
  );
  const loading =
    library.loading || Boolean(boardToken && contents.status === "loading");
  const source = boardToken ? contents.items : library.creatives;
  const visible = useMemo(
    () =>
      filterCreativeItems(source, filters, {
        scopedBrandToken: requestedBrand,
      }),
    [source, filters, requestedBrand],
  );
  const allCreatives = useMemo(
    () =>
      Array.from(
        new Map(
          [...library.creatives, ...contents.items].map((item) => [
            item.token,
            item,
          ]),
        ).values(),
      ),
    [library.creatives, contents.items],
  );
  const hasFilters = Object.entries(filters).some(([key, value]) =>
    key === "query" ? Boolean(value.trim()) : value !== "all",
  );
  const canCreateBoard = library.brands.some((brand) => brand.canEdit);
  useEffect(
    () =>
      setFilters((current) => ({ ...current, brand: requestedBrand ?? "all" })),
    [requestedBrand],
  );
  useEffect(() => setLimit(CREATIVE_PAGE_SIZE), [filters, boardToken]);
  useEffect(() => {
    if (!boardToken) {
      return;
    }
    setFilters((current) => ({
      ...EMPTY_CREATIVE_FILTERS,
      brand: current.brand !== "all" ? current.brand : (requestedBrand ?? "all"),
    }));
  }, [boardToken, requestedBrand]);
  useEffect(() => {
    if (!focused) {
      setDetail(null);
      setDetailError(false);
      return;
    }
    let active = true;
    // A refresh of the same creative keeps it open; only another creative clears it.
    setDetail((current) => (current?.token === focused ? current : null));
    setDetailError(false);
    void creativeLibraryApi
      .creative(focused)
      .then((item) => {
        if (active) setDetail(item);
      })
      .catch(() => {
        if (active) setDetailError(true);
      });
    return () => {
      active = false;
    };
  }, [focused, refresh]);
  function reload() {
    clearActionError();
    setRefresh((value) => value + 1);
    void library.reload();
  }
  function chooseBoard(token: string | null) {
    setParams((previous) => selectCreativeBoard(previous, token));
  }
  function useBoard() {
    if (!board || blocked) return;
    void start(board.token, {
      boardToken: board.token,
      brandProfileToken:
        filters.brand !== "all" &&
        (board.applicableBrandTokens.includes(filters.brand) ||
          board.brandToken === filters.brand)
          ? filters.brand
          : board.brandToken,
    });
  }
  function openSubject(subject: CreativeSubject) {
    setUpload(false);
    setDetail(null);
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("view", "creatives");
      next.set("brand", subject.brandToken);
      next.set("models", "1");
      next.set("subject", subject.token);
      next.delete("creative");
      return next;
    });
  }
  function closeDetail() {
    setDetail(null);
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("creative");
        return next;
      },
      { replace: true },
    );
  }
  function openBoardSettings() {
    if (board?.canEdit) setBoardEditor({ open: true, value: board });
  }
  return (
    <section
      className={cn(
        "flex min-w-0 flex-col gap-6 md:flex-row md:gap-8",
        className,
      )}
      data-testid="creative-library-creatives"
    >
      <CreativeBoardNavigation
        boards={library.boards}
        activeToken={boardToken}
        selectedBoard={board}
        selectedCount={
          boardToken && contents.status === "ready"
            ? contents.items.length
            : undefined
        }
        creatives={allCreatives}
        loading={library.loading}
        error={library.error}
        canCreate={canCreateBoard}
        onSelect={chooseBoard}
        onNew={() => setBoardEditor({ open: true, value: null })}
        onNewBrand={() => setNewBrand(true)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <CreativeWorkspaceHeader
          boardToken={boardToken}
          board={board}
          status={contents.status}
          disabled={loading || blocked || Boolean(starting) || library.error}
          starting={Boolean(starting)}
          onSettings={openBoardSettings}
          onCreate={useBoard}
          onUpload={() => setUpload(true)}
        />
        {(library.error ||
          contents.refreshFailed ||
          detailError ||
          actionError ||
          blocked) && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/30 p-4"
            role="alert"
            data-testid="creative-workspace-error"
          >
            <Text size="sm" className="text-destructive">
              {t(
                blocked
                  ? contents.status === "unavailable"
                    ? "creativeLibrary.boardUnavailableHint"
                    : "creativeLibrary.boardLoadFailed"
                  : actionError
                    ? "creativeLibrary.startFailed"
                    : "creativeLibrary.loadFailed",
              )}
            </Text>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reload}>
                {t("creativeLibrary.retry")}
              </Button>
              {boardToken && (
                <Button
                  variant="outline"
                  onClick={() => chooseBoard(null)}
                  data-testid="creative-board-exit"
                >
                  {t("creativeLibrary.allCreatives")}
                </Button>
              )}
            </div>
          </div>
        )}
        {!blocked && (
          <>
            <CreativeWorkspaceFilters
              value={filters}
              brands={library.brands}
              onChange={setFilters}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Text
                size="xs"
                color="muted"
                data-testid="creative-workspace-count"
              >
                {!loading &&
                  !library.error &&
                  t("creativeLibrary.visibleCreatives", {
                    count: visible.length,
                  })}
              </Text>
              <div className="flex flex-wrap gap-2">
                {filters.brand !== "all" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/knowledge?view=creatives&brand=${filters.brand}&models=1${boardToken ? `&board=${boardToken}` : ""}`,
                      )
                    }
                    data-testid="creative-manage-subjects"
                  >
                    {t("creativeLibrary.subjects")}
                  </Button>
                )}
                {boardToken && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUpload(true)}
                    data-testid="creative-board-upload"
                  >
                    <Plus className="h-4 w-4" />
                    {t("creativeLibrary.uploadReference")}
                  </Button>
                )}
                {hasFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilters({ ...EMPTY_CREATIVE_FILTERS });
                      setParams(
                        (previous) => {
                          const next = new URLSearchParams(previous);
                          next.delete("brand");
                          return next;
                        },
                        { replace: true },
                      );
                    }}
                    data-testid="creative-clear-filters"
                  >
                    {t("creativeLibrary.clearFilters")}
                  </Button>
                )}
              </div>
            </div>
            {loading ? (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {[0, 1, 2].map((n) => (
                  <Skeleton key={n} className="h-64 rounded-2xl" />
                ))}
              </div>
            ) : (
              (!library.error || library.hasData) &&
              (visible.length ? (
                <>
                  <div
                    className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
                    data-testid="creative-workspace-grid"
                  >
                    <CreativeItemCards
                      items={visible.slice(0, limit)}
                      brands={library.brands}
                      board={board}
                      onDetail={(item) =>
                        setParams((previous) => {
                          const next = new URLSearchParams(previous);
                          next.set("creative", item.token);
                          return next;
                        })
                      }
                    />
                  </div>
                  {visible.length > limit && (
                    <Button
                      variant="outline"
                      className="self-center"
                      onClick={() =>
                        setLimit((value) => value + CREATIVE_PAGE_SIZE)
                      }
                      data-testid="creative-show-more"
                    >
                      {t("creativeLibrary.showMoreCreatives")}
                    </Button>
                  )}
                </>
              ) : (
                <CreativeLibraryEmptyState
                  kind="creatives"
                  title={t(
                    hasFilters
                      ? "creativeLibrary.noMatches"
                      : boardToken
                        ? "creativeLibrary.emptyBoard"
                        : "creativeLibrary.empty.creatives",
                  )}
                  description={t(
                    hasFilters
                      ? "creativeLibrary.noMatchesHint"
                      : boardToken
                        ? "creativeLibrary.emptyBoardHint"
                        : "creativeLibrary.creativesHint",
                  )}
                  actionLabel={
                    !hasFilters
                      ? boardToken
                        ? board?.canEdit
                          ? t("creativeLibrary.boardSettings")
                          : undefined
                        : t("creativeLibrary.uploadReference")
                      : undefined
                  }
                  onCreate={
                    boardToken ? openBoardSettings : () => setUpload(true)
                  }
                />
              ))
            )}
          </>
        )}
      </div>
      <BoardEditor
        open={boardEditor.open}
        board={boardEditor.value}
        brands={library.brands}
        creatives={allCreatives}
        onClose={() => setBoardEditor({ open: false, value: null })}
        onSaved={(saved) => {
          reload();
          if (saved && !boardEditor.value) chooseBoard(saved.token);
        }}
      />
      <BrandEditor
        open={newBrand}
        brand={null}
        brands={library.brands}
        creatives={allCreatives}
        onClose={() => setNewBrand(false)}
        onSaved={reload}
      />
      <CreativeUpload
        open={upload}
        brands={library.brands}
        products={library.products}
        presetBrandToken={
          filters.brand === "all" ? board?.brandToken : filters.brand
        }
        onClose={() => setUpload(false)}
        onSaved={reload}
        onOpenSubject={openSubject}
      />
      <CreativeDetail
        subjects={library.subjects}
        subjectsReady={!library.loading && !library.error}
        item={detail}
        brands={library.brands}
        products={library.products}
        onOpenSubject={openSubject}
        onClose={closeDetail}
        onSaved={reload}
      />
      <CreativeLibrarySubjects
        brands={library.brands}
        subjects={library.subjects}
        creatives={allCreatives}
        onSaved={reload}
        onUse={(subject) =>
          void start(subject.token, {
            brandProfileToken: subject.brandToken,
            subjectToken: subject.token,
            referenceTokens: [],
            intendedUse: "internal",
          })
        }
      />
    </section>
  );
}
